import { Document, Page, View, Text, Link } from '@react-pdf/renderer';
import { format } from 'date-fns';

import {
  createStylesheet,
  getCardSourceLink,
  getLensIconComponent,
  getTokenHex,
  registerFonts
} from '@util/helpers';
import PDFBrandingFooter from './pdf-branding-footer.component';
import PDFBrandingHeader from './pdf-branding-header.component';
import PDFPageDivider from './pdf-page-divider.component';

registerFonts();

const styles = createStylesheet();

/** @param {{ insight: Insight, initiative: Initiative, fullCards: Card[] }} */
export default function InsightPDF({ insight, initiative, fullCards, userName, tenantName }) {
  return (
    <Document
      title={`${insight?.headline} - ${format(insight?.created_at, 'PP')}`}
      producer={tenantName}
      author={userName}
      creator={userName}
    >
      <Page
        style={{ ...styles.page, backgroundColor: getTokenHex(['surface', 'containerLow']) }}
        size={[800, 1048]}
      >
        <PDFBrandingHeader title='Insight' />

        <View style={styles.contentContainer}>
          <View style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>{insight?.headline}</Text>
            <Text style={{ fontSize: '18px', fontWeight: 'normal' }}>{insight?.subheadline}</Text>
            <Text style={{ fontSize: '14px', color: getTokenHex(['surface', 'onSurfaceVariant']) }}>
              Last updated on {format(insight?.updated_at, 'cccc, LLLL d, yyyy')}
            </Text>
            <AboutThisLensPdf initiative={initiative} />
          </View>
          <PDFPageDivider />

          <View style={{ display: 'flex', flexDirection: 'column', gap: '44px' }}>
            {insight?.sections?.map((section, index) => (
              <View
                key={`${section?.title}-${index + 1}`}
                style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
              >
                <Text
                  style={{
                    fontSize: '20px',
                    fontWeight: 'semibold',
                    color: getTokenHex(['surface', 'onSurface'])
                  }}
                >
                  {section?.title}
                </Text>
                {renderContentWithSources(section?.content, insight, fullCards)}
              </View>
            ))}
          </View>

          <PDFPageDivider />

          <Text style={{ fontWeight: 'bold', fontSize: '20px' }}>Sources</Text>
          <View style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {insight?.cards?.map((_, i) => {
              const sourceNumber = i + 1;
              const card = getFullCardBySourceNumber(sourceNumber, insight, fullCards);
              const link = buildSourceLink(card);

              return (
                <View
                  key={link}
                  style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}
                >
                  <PDFSourceButton link={link}>{sourceNumber}</PDFSourceButton>

                  <View style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Link
                      src={link}
                      style={{ textDecoration: 'none', fontSize: '16px', fontWeight: 500 }}
                    >
                      {card?.source_data?.title}
                    </Link>
                    <Text
                      style={{
                        fontSize: '14px',
                        color: getTokenHex(['surface', 'onSurfaceVariant'])
                      }}
                    >
                      {card?.headline}
                    </Text>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
        <PDFBrandingFooter />
      </Page>
    </Document>
  );
}

/** @param {{ initiative: Initiative }} */
const AboutThisLensPdf = ({ initiative }) => {
  if (!initiative) {
    return null;
  }

  const LensIcon = getLensIconComponent(initiative, undefined, true);
  return (
    <View
      style={{
        marginTop: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        border: `1px solid ${getTokenHex(['surface', 'outlineVariant'])}`,
        backgroundColor: getTokenHex(['surface', 'containerLowest']),
        borderRadius: '12px',
        padding: '16px'
      }}
    >
      <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
        <LensIcon color={initiative?.color} />
        <Text style={{ fontWeight: 600 }}>{initiative?.title}</Text>
      </View>
      <Text
        style={{
          color: getTokenHex(['surface', 'onSurfaceVariant']),
          fontSize: '16px'
        }}
      >
        {initiative?.understanding}
      </Text>
      <Text
        style={{
          fontWeight: 500,
          fontSize: '14px',
          color: getTokenHex(['surface', 'onSurfaceVariant'])
        }}
      >
        Created by{' '}
        <Text
          style={{
            display: 'inline',
            color: getTokenHex(['brandPrimary', 'primary'])
          }}
        >
          {initiative?.creator?.name}
        </Text>
      </Text>
    </View>
  );
};

const PDFSourceButton = ({ children, link }) => {
  return (
    <Link src={link}>
      <Text
        style={{
          color: getTokenHex(['brandPrimary', 'primary']),
          fontSize: '10px',
          fontWeight: 'semibold',
          textAlign: 'center',
          paddingBottom: '8px'
        }}
      >
        {children}
      </Text>
    </Link>
  );
};

const getFullCardBySourceNumber = (sourceNumber, insight, fullCards) => {
  const cardId = insight?.cards?.[sourceNumber - 1]?.id;
  return fullCards?.[cardId];
};

const buildSourceLink = (card) => {
  return `https://app.fora.day${getCardSourceLink(card)}`;
};

const renderContentWithSources = (content, insight, fullCards) => {
  if (!content) return null;

  const parts = content.split(/(`[^`]*`)/g);

  return (
    <Text
      style={{
        fontSize: '18px',
        lineHeight: '175%',
        color: getTokenHex(['surface', 'onSurfaceVariant'])
      }}
    >
      {parts.map((part) => {
        const sourceMatch = part.match(/`([^`]*)`/);
        if (!sourceMatch) {
          return part;
        }

        const sourceNumber = sourceMatch[1];
        const card = getFullCardBySourceNumber(sourceNumber, insight, fullCards);
        const link = buildSourceLink(card);

        return (
          <PDFSourceButton
            key={part?.substring(0, 10)}
            link={link}
          >
            {sourceNumber}
          </PDFSourceButton>
        );
      })}
    </Text>
  );
};
